/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file state/store for in progress apps widget
 * @module Epic.AppOrchard.State.InProgressApps
 */

import { buildSharedState } from "@epic/react-redux-booster";
import store from "ao/store";
import { ILiveApp } from "ao/types";
import { searchHasMatch } from "ao/utils/helpers";

/// TYPES ///

export interface IState {
	// Map between the org ID and the in apps for a customer org
	readonly appsForOrg: Map<number, ILiveApp[]> | undefined;
	// Whether app data has been loaded for a customer org
	readonly hasLoadedForOrg: Map<number, boolean> | undefined;
}

/**
 * Search params to identify the apps to select from the store
 */
interface IAppsSelectorParams {
	orgId: number;
	searchTerm: string;
}

/// INIT ///

/**
 * Returns an empty initial state
 */
export function getInitialState(): IState {
	return {
		hasLoadedForOrg: undefined,
		appsForOrg: undefined,
	};
}

//#region REDUCERS

/**
 * Make JS Date objects from string dates loaded from the server
 * @param app app to transform
 * @returns app with JS Date objects
 */
function makeJSDates(app: ILiveApp): ILiveApp {
	return {
		...app,
		markedLiveOn: app.markedLiveOn && new Date(app.markedLiveOn),
	};
}

/**
 * Update in progress apps for a customer org and mark apps as loaded from server
 * @param state current state
 * @param params reducer params (org ID and apps to add)
 * @returns updated state
 */
export function updateAppsAfterLoad(state: IState, params: { orgId: number; apps: ILiveApp[] }): IState {
	const { orgId, apps } = params;

	// update state to indicate we've loaded data for this org
	var loadedMap = state.hasLoadedForOrg
		? new Map<number, boolean>([...Array.from(state.hasLoadedForOrg), [orgId, true]])
		: new Map<number, boolean>([[orgId, true]]);

	const appsEntryForOrg = apps.map((a) => makeJSDates(a));

	// update data loaded for the org
	const appsMap = state.appsForOrg
		? new Map<number, ILiveApp[]>([...Array.from(state.appsForOrg), [orgId, appsEntryForOrg]])
		: new Map<number, ILiveApp[]>([[orgId, appsEntryForOrg]]);

	return {
		hasLoadedForOrg: loadedMap,
		appsForOrg: appsMap,
	};
}
/**
 * Clear loaded flag to force components to reload apps from server,
 * @param state current state
 * @param params reducer params (org ID)
 * @returns updated state
 */
export function clearLoaded(state: IState, orgId: number): IState {
	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return state;
	}

	// remove this org's entry
	const appsMap = new Map<number, ILiveApp[]>([
		...Array.from(state.appsForOrg!)
			// first entry is org ID for a given map key value pair
			.filter((appsForOrg) => appsForOrg[0] !== orgId),
	]);
	const loadedMap = new Map<number, boolean>([
		...Array.from(state.hasLoadedForOrg!).filter((loadedForOrg) => loadedForOrg[0] !== orgId),
	]);

	return {
		hasLoadedForOrg: loadedMap,
		appsForOrg: appsMap,
	};
}

//#endregion

//#region SELECTORS

/**
 * Check whether data has been loaded for a customer org
 * @param state current state
 * @param orgId: customer org id
 * @returns
 */
export function getHasLoaded(state: IState, orgId: number): boolean {
	return state.hasLoadedForOrg !== undefined && state.hasLoadedForOrg.get(orgId) === true;
}

function hasDataForOrg(state: IState, orgId: number): boolean {
	return state.appsForOrg !== undefined && state.appsForOrg.get(orgId) !== undefined;
}

/**
 * Helper to filter apps by search term
 * @param searchTerm search term to filter apps on
 * @param app app being considered for inclusion in results
 * @returns true if passes filter else false
 */
function filterBySearchTerm(searchTerm: string, app: ILiveApp): boolean {
	return (
		searchHasMatch(searchTerm, app.appName) ||
		searchHasMatch(searchTerm, app.appOrgName) ||
		searchHasMatch(searchTerm, app.prodClientId) ||
		searchHasMatch(searchTerm, app.nonProdClientId)
	);
}

/**
 * Shared helper that filters to apps of a given type + search term filtering
 *
 * Checks if data has been loaded for the org too
 * @param state current state
 * @param orgId customer org ID
 * @param searchTerm search term to further filter matching apps
 * @param filterFn function to filter to type of app
 * @returns list of apps matching filters
 */
function getAppsOfType(
	state: IState,
	orgId: number,
	searchTerm: string,
	filterFn: ((app: ILiveApp) => boolean) | undefined,
): ILiveApp[] {
	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return [];
	}
	let apps = state.appsForOrg!.get(orgId)!;
	if (filterFn && apps.length > 0) {
		apps = apps.filter(filterFn);
	}

	if (searchTerm && apps.length > 0) {
		apps = apps.filter((a) => filterBySearchTerm(searchTerm, a));
	}

	return apps;
}

/**
 * Get all live apps
 * @param state current state
 * @param params filter params
 */
export function getAllApps(state: IState, params: IAppsSelectorParams): ILiveApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(state, orgId, searchTerm, undefined);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

/**
 * Get apps that don't have reviews for the org yet
 * @param state current state
 * @param params filter params
 */
export function getAppsWithoutReviews(state: IState, params: IAppsSelectorParams): ILiveApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(state, orgId, searchTerm, (a) => a.numReviewsForOrg === 0);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

/**
 * Get number of apps that went live in the last X months
 * @param state current state
 * @param params filter params
 */
export function getNumAppsLiveInLastXMonths(
	state: IState,
	params: { orgId: number; monthsAgo: number },
): number | undefined {
	const { orgId, monthsAgo } = params;

	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return undefined;
	}

	const today = new Date();
	const xMonthsAgoInst = new Date(
		today.getFullYear(),
		today.getMonth() - monthsAgo,
		today.getDay(),
	).getTime();

	const apps = getAppsOfType(
		state,
		orgId,
		"",
		(a) => a.markedLiveOn !== null && a.markedLiveOn.getTime() >= xMonthsAgoInst,
	);
	return apps.length;
}

//#endregion

/// BUILD IT ///

const builtState = buildSharedState({
	init: getInitialState,
	reducers: {
		updateAppsAfterLoad,
		clearLoaded,
	},
	selectors: {
		getHasLoaded,
		getAllApps,
		getAppsWithoutReviews,
		getNumAppsLiveInLastXMonths,
	},
});
store.addSharedState(builtState.sharedState, "liveApps");

export const { actionCreators: liveAppsActions, useSharedState: useLiveAppsState } = builtState;
